import { forwardRef, useImperativeHandle, useRef, useEffect, useState } from "react";
import { Eraser } from "lucide-react";

export const SignaturePad = forwardRef(({ height = 170 }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const setup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  };

  useEffect(() => { setup(); }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  useImperativeHandle(ref, () => ({
    getSignature: () => (hasDrawn ? canvasRef.current.toDataURL("image/png") : null),
    clear,
    isEmpty: () => !hasDrawn,
  }));

  return (
    <div className="space-y-1.5">
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          style={{ width: "100%", height, touchAction: "none", display: "block", cursor: "crosshair" }}
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
        />
        {!hasDrawn && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">
            وقّع هنا بالإصبع أو الماوس
          </span>
        )}
      </div>
      <button type="button" data-testid="signature-clear-button" onClick={clear}
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-600 transition">
        <Eraser className="h-3.5 w-3.5" /> مسح التوقيع
      </button>
    </div>
  );
});
